import { Emitter } from '@wonderlandengine/api';
import { networkManager } from '@wonderlandcloud/client';
import Logger from '../utils/logger.js';
import { Events } from '../network-events.js';

const MODULE = 'VideoShare';

export interface VideoShareContext {
    vidsharePeerConnections: Map<number, RTCPeerConnection>;
    vidrecPeerConnections: Map<number, RTCPeerConnection>;
    vidshareCandidatesMap: Map<number, RTCIceCandidateInit[]>;
    vidrecCandidatesMap: Map<number, RTCIceCandidateInit[]>;
    activeVideoStreams: Map<number, MediaStream>;
    webRTCSupported: boolean;
    makingVidshareOffer: boolean;
    sharingVideo: boolean;
    onShareEvent: Emitter<[number, RTCTrackEvent]>;
    onShareStopEvent?: Emitter<[number]>;
    remoteUsers: Map<number | string, any>;
    playerUserId: number;
    masterSharerId: number;
    serverNetworkId: number;
    addTrackedRTCEventListener: (
        element: RTCPeerConnection,
        event: 'track',
        handler: (this: RTCPeerConnection, ev: RTCTrackEvent) => any
    ) => void;
}

export async function handleVidshareOffer(
    context: VideoShareContext,
    senderId: number,
    offer: any
): Promise<void> {
    createVidrecPeerConnection(context, senderId);

    Logger.debug(
        'Peer connection created for receiving shared video from: ' + senderId,
        MODULE
    );

    await acceptVidshareOffer(context, senderId, offer);
}

export function createVidrecPeerConnection(
    context: VideoShareContext,
    senderId: number
): RTCPeerConnection {
    const iceServers: RTCIceServer[] = [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:3478' },
        { urls: 'stun:stun2.l.google.com:19302' },
        { urls: 'stun:stun3.l.google.com:3478' },
        { urls: 'stun:stun4.l.google.com:19302' },
        { urls: 'stun:stun4.l.google.com:3478' },
    ];

    // Add TURN servers if available
    const turnServers = (context as any).getTurnIceServers?.();
    if (turnServers?.length) {
        iceServers.push(...turnServers);
    }

    const vrpc = new RTCPeerConnection({
        iceServers,
    });

    destroyVidrecConnection(context, senderId);

    context.vidrecPeerConnections.set(senderId, vrpc);

    vrpc.onicegatheringstatechange = (event) => {
        Logger.debug(
            'Vidrec: ICE gathering state changed',
            MODULE,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            event.target.iceGatheringState
        );
    };
    vrpc.onicecandidate = (event) => {
        Logger.debug('Vidrec: ICE candidate: ' + event.candidate, MODULE);
        if (event.candidate) {
            networkManager.client?.sendViaWs({
                type: Events.PLAYER_SHARING_STREAM_ANSWER_ICE,
                data: {
                    candidate: event.candidate,
                    receiverId: senderId,
                },
            });
        }
    };
    vrpc.onicecandidateerror = (event) => {
        Logger.warn('Vidrec: ICE candidate error', MODULE, event);
    };
    vrpc.onsignalingstatechange = () => {
        Logger.debug('Vidrec: Signaling state changed: ' + vrpc?.signalingState, MODULE);
        if (
            vrpc &&
            (vrpc.signalingState === 'have-remote-offer' ||
                vrpc.signalingState === 'stable')
        ) {
            context.vidrecCandidatesMap.get(senderId)?.forEach((can) => {
                vrpc
                    ?.addIceCandidate(can)
                    .then((val) => Logger.debug('Added ICE candidate', MODULE, val, can))
                    .catch((err) => Logger.warn('Could not add ICE candidate', MODULE, err));
            });
            context.vidrecCandidatesMap.set(senderId, []);
        }
    };
    vrpc.onconnectionstatechange = () => {
        Logger.debug('Vidrec: Connection state changed: ' + vrpc.connectionState, MODULE);
        if (vrpc.connectionState === 'failed') {
            vrpc.restartIce();
        }
        if (vrpc?.connectionState === 'failed') {
            // we have a failed WebRTC connection. The following boolean is probably overkill. See where this affects...
            context.webRTCSupported = false;
            Logger.warn('Vidrec: Failed to establish RTCPeerConnection for video sharing', MODULE);
        }
    };
    vrpc.oniceconnectionstatechange = () => {
        Logger.debug('Vidrec: ICE connection state changed:' + vrpc.iceConnectionState, MODULE);
        if (vrpc.iceConnectionState === 'failed') {
            vrpc.restartIce();
        }
    };

    const trackHandler = (e: RTCTrackEvent) => {
        const stream = e.streams[0];
        const audioCount = stream?.getAudioTracks?.().length ?? 0;
        const videoCount = stream?.getVideoTracks?.().length ?? 0;
        Logger.debug(
            `Vidrec track received from ${senderId}: kind=${e.track?.kind}, streamAudio=${audioCount}, streamVideo=${videoCount}`,
            MODULE
        );

        // Store the stream for late-joining listeners
        if (e.streams.length > 0) {
            context.activeVideoStreams.set(senderId, e.streams[0]);
        }
        context.onShareEvent.notify(senderId, e);
    };
    context.addTrackedRTCEventListener(vrpc, 'track', trackHandler);

    return vrpc;
}

// Loop through all known peers and create a peer connection for each one
export function createVidsharePeerConnections(context: VideoShareContext): void {
    for (const key of context.remoteUsers.keys()) {
        createVidsharePeerConnection(context, Number(key));
    }
    createVidsharePeerConnection(context, context.playerUserId);
}

export function createVidsharePeerConnection(
    context: VideoShareContext,
    playerId: number
): RTCPeerConnection {
    context.sharingVideo = true;
    const iceServers: RTCIceServer[] = [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:3478' },
        { urls: 'stun:stun2.l.google.com:19302' },
        { urls: 'stun:stun3.l.google.com:3478' },
        { urls: 'stun:stun4.l.google.com:19302' },
        { urls: 'stun:stun4.l.google.com:3478' },
    ];

    // Add TURN servers if available
    const turnServers = (context as any).getTurnIceServers?.();
    if (turnServers?.length) {
        iceServers.push(...turnServers);
    }

    const vidsharePC = new RTCPeerConnection({
        iceServers,
    });

    destroyVidshareConnection(context, playerId);

    context.vidsharePeerConnections.set(playerId, vidsharePC);
    vidsharePC.onicegatheringstatechange = (event) => {
        Logger.debug(
            'Vidshare: ICE gathering state changed',
            MODULE,
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            event.target.iceGatheringState
        );
    };
    vidsharePC.onicecandidate = (event) => {
        Logger.debug('Vidshare: ICE candidate: ' + event.candidate, MODULE);
        if (event.candidate) {
            networkManager.client?.sendViaWs({
                type: Events.PLAYER_SHARING_STREAM_OFFER_ICE,
                data: {
                    receiverId: playerId,
                    candidate: event.candidate,
                },
            });
        }
    };
    vidsharePC.onicecandidateerror = (event) => {
        Logger.warn('Vidshare: ICE candidate error', MODULE, event);
    };
    vidsharePC.onsignalingstatechange = () => {
        Logger.debug('Vidshare: Signaling state changed: ' + vidsharePC.signalingState, MODULE);
        if (
            vidsharePC &&
            (vidsharePC.signalingState === 'have-remote-offer' ||
                vidsharePC.signalingState === 'stable')
        ) {
            context.vidshareCandidatesMap.get(playerId)?.forEach((can) => {
                if (vidsharePC) {
                    vidsharePC
                        .addIceCandidate(can)
                        .then((val) => Logger.debug('Added ICE candidate', MODULE, val, can))
                        .catch((err) => Logger.warn('Could not add ICE candidate', MODULE, err));
                }
            });
            context.vidshareCandidatesMap.set(playerId, []);
        }
    };
    vidsharePC.onnegotiationneeded = async () => {
        Logger.debug('Vidshare: Negotiation needed', MODULE);
        if (vidsharePC.signalingState !== 'stable') {
            return;
        }
        context.makingVidshareOffer = true;
        await startVidshareNegotiation(context, playerId, vidsharePC);
        context.makingVidshareOffer = false;
    };
    vidsharePC.onconnectionstatechange = () => {
        Logger.debug('Vidshare: Connection state changed: ' + vidsharePC.connectionState, MODULE);
        if (vidsharePC.connectionState === 'failed') {
            vidsharePC.restartIce();
        }
        if (vidsharePC.connectionState === 'failed') {
            // we have a failed WebRTC connection
            context.webRTCSupported = false;
            Logger.warn('Vidshare: Failed to establish RTCPeerConnection for video sharing', MODULE);
        }
    };
    vidsharePC.oniceconnectionstatechange = () => {
        Logger.debug('Vidshare: ICE connection state changed: ' + vidsharePC.iceConnectionState, MODULE);
        if (vidsharePC.iceConnectionState === 'failed') {
            vidsharePC.restartIce();
        }
    };

    return vidsharePC;
}

export async function acceptVidshareOffer(
    context: VideoShareContext,
    senderId: number,
    offer: any
): Promise<void> {
    Logger.debug('Accepting vidshare offer from: ' + senderId, MODULE);

    const offersdp = JSON.parse(JSON.stringify(offer));

    const vrpc = context.vidrecPeerConnections.get(senderId);

    await vrpc
        ?.setRemoteDescription(offersdp)
        .then(() => vrpc?.createAnswer())
        .then(async (answer) => {
            await vrpc?.setLocalDescription(answer);
            Logger.debug('Created vidshare answer, sending...', MODULE);

            // Message through server channel
            networkManager.client?.sendViaWs({
                type: Events.PLAYER_SHARING_STREAM_ANSWER,
                data: {
                    receiverId: senderId,
                    answer: answer,
                },
            });
        });
}

export async function startVidshareNegotiations(context: VideoShareContext): Promise<void> {
    for (const key of context.remoteUsers.keys()) {
        const numKey = Number(key);
        const peerCon = context.vidsharePeerConnections.get(numKey);
        if (peerCon) {
            await startVidshareNegotiation(context, numKey, peerCon);
        }
    }
    const selfCon = context.vidsharePeerConnections.get(context.playerUserId);

    if (selfCon) {
        await startVidshareNegotiation(context, context.playerUserId, selfCon);
    }
}

export async function startVidshareNegotiation(
    context: VideoShareContext,
    rec: number,
    vidsharePC: RTCPeerConnection
): Promise<void> {
    const offer = await createVidshareOffer(vidsharePC);
    Logger.debug('Created vidshare offer for receiver: ' + rec, MODULE);
    if (offer) {
        await vidsharePC.setLocalDescription(offer);
        if (rec === context.serverNetworkId) {
            // This is a request to have the server re-broadcast instead of peer-to-peer with other users
            networkManager.client?.sendViaWs({
                type: Events.PLAYER_BROADCASTING_STREAM_OFFER,
                data: {
                    receiverId: rec,
                    data: offer,
                },
            });
        } else {
            networkManager.client?.sendViaWs({
                type: Events.PLAYER_SHARING_STREAM_OFFER,
                data: {
                    receiverId: rec,
                    data: offer,
                },
            });
        }
    }
}

export async function createVidshareOffer(
    vidsharePC: RTCPeerConnection
): Promise<RTCSessionDescriptionInit> {
    return await vidsharePC.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
    });
}

function tuneOpusSdp(sdp: string): string {
    const opusPayloadTypes = new Set<string>();
    const rtpmapRegex = /^a=rtpmap:(\d+)\s+opus\/48000(?:\/2)?\s*$/gim;

    let rtpmapMatch: RegExpExecArray | null;
    while ((rtpmapMatch = rtpmapRegex.exec(sdp)) !== null) {
        opusPayloadTypes.add(rtpmapMatch[1]);
    }

    if (opusPayloadTypes.size === 0) {
        return sdp;
    }

    return sdp.replace(/^a=fmtp:(\d+)\s+([^\r\n]*)$/gim, (line, payloadType, params) => {
        if (!opusPayloadTypes.has(String(payloadType))) {
            return line;
        }

        const orderedKeys: string[] = [];
        const values = new Map<string, string>();

        const tokens = String(params)
            .split(';')
            .map((token) => token.trim())
            .filter((token) => token.length > 0);

        for (const token of tokens) {
            const eqIndex = token.indexOf('=');
            if (eqIndex === -1) {
                const keyOnly = token.toLowerCase();
                if (!values.has(keyOnly)) {
                    orderedKeys.push(keyOnly);
                }
                values.set(keyOnly, '');
                continue;
            }

            const key = token.slice(0, eqIndex).trim().toLowerCase();
            const value = token.slice(eqIndex + 1).trim();
            if (!values.has(key)) {
                orderedKeys.push(key);
            }
            values.set(key, value);
        }

        const upsert = (key: string, value: string) => {
            if (!values.has(key)) {
                orderedKeys.push(key);
            }
            values.set(key, value);
        };

        upsert('useinbandfec', values.get('useinbandfec') || '1');
        upsert('stereo', '1');
        upsert('sprop-stereo', '1');
        upsert('maxaveragebitrate', '510000');

        const serialized = orderedKeys
            .map((key) => {
                const value = values.get(key) || '';
                return value.length > 0 ? `${key}=${value}` : key;
            })
            .join('; ');

        return `a=fmtp:${payloadType} ${serialized}`;
    });
}

export function claimMasterShareRole(context: VideoShareContext): void {
    if (context.sharingVideo) {
        networkManager.client?.sendViaWs({
            type: Events.PLAYER_CLAIMING_MASTER_SHARE_ROLE,
            data: [],
        });
    }
}

export function stopVidshare(context: VideoShareContext): void {
    if (context.sharingVideo) {
        for (const key of context.remoteUsers.keys()) {
            destroyVidshareConnection(context, Number(key));
        }
        destroyVidshareConnection(context, context.playerUserId);

        const stopEventType = context.masterSharerId === context.playerUserId
            ? Events.PLAYER_RELEASING_MASTER_SHARE_ROLE
            : Events.PLAYER_STOPPED_SHARING_STREAM;

        networkManager.client?.sendViaWs({
            type: stopEventType,
            data: [],
        });

        // Local immediate cleanup for legacy P2P flow
        context.activeVideoStreams.delete(context.playerUserId);
        context.onShareStopEvent?.notify(context.playerUserId);

        context.sharingVideo = false;
    }
}

export function destroyVidshareConnection(
    context: VideoShareContext,
    peerId: number
): void {
    const vsPC = context.vidsharePeerConnections.get(peerId);

    if (vsPC) {
        vsPC.close();
        context.vidsharePeerConnections.delete(peerId);
        context.vidshareCandidatesMap.set(peerId, []);
    }
}

export function destroyVidrecConnection(
    context: VideoShareContext,
    peerId: number
): void {
    const vrPC = context.vidrecPeerConnections.get(peerId);

    if (vrPC) {
        vrPC.close();
        context.vidrecPeerConnections.delete(peerId);
        context.vidrecCandidatesMap.set(peerId, []);
        // Clean up the active stream for this peer
        context.activeVideoStreams.delete(peerId);
    }
}
